/**
 * Dwunastościenna kostka Superfarmera.
 *
 * Ściana to **biała kreska na barwnej bryle**, tak jak na kostce z pudełka —
 * a nie zdjęcie figurki. Figurki zostają na kartonikach w stadzie i właśnie
 * dlatego kostka musi wyglądać inaczej: gdyby na obu miejscach stało to samo,
 * „wyrzuciłem owcę" i „mam owcę" zlewałyby się w jeden obrazek.
 *
 * Barwa kostki nie jest ozdobą: koń wypada wyłącznie na czerwonej, krowa
 * wyłącznie na zielonej, więc gracz musi widzieć, która jest która.
 */

import { useState } from "react";

export type Sciana = "krolik" | "owca" | "swinia" | "krowa" | "kon" | "wilk" | "lis";

const OPISY: Record<Sciana, string> = {
  krolik: "królik",
  owca: "owca",
  swinia: "świnia",
  krowa: "krowa",
  kon: "koń",
  wilk: "wilk",
  lis: "lis",
};

export function KostkaZwierzat({
  sciana,
  kolor,
  rzucana = false,
}: {
  sciana: Sciana | null;
  kolor: "zielona" | "czerwona";
  rzucana?: boolean;
}) {
  return (
    <div
      className={[
        "w-24 h-24 rounded-3xl grid place-items-center overflow-hidden",
        "border-4",
        rzucana ? "kostka-rzut" : "",
      ].join(" ")}
      style={{
        background: `var(--kostka-${kolor})`,
        borderColor: "var(--plansza-obrys)",
      }}
      aria-label={sciana ? OPISY[sciana] : "kostka"}
    >
      {sciana ? (
        <Glif sciana={sciana} opis={OPISY[sciana]} />
      ) : (
        <span className="text-3xl" style={{ color: "var(--kostka-oczko)" }}>
          ?
        </span>
      )}
    </div>
  );
}

/**
 * Kreska na ścianie kostki, z zapasem na wypadek braku pliku.
 *
 * Glify to białe kontury z kanałem alfa (`public/superfarmer/kostka/`), więc
 * kładą się wprost na barwie kostki i nie wymagają osobnej wersji na zieloną
 * i czerwoną. Zepsuty obrazek w środku rundy wygląda jak awaria gry, a nie jak
 * brakujący plik — dlatego zamiast niego pojawia się nazwa zwierzęcia.
 */
function Glif({ sciana, opis }: { sciana: Sciana; opis: string }) {
  const [blad, setBlad] = useState(false);

  if (blad) {
    return (
      <span className="text-sm text-center px-1" style={{ color: "var(--kostka-oczko)" }}>
        {opis}
      </span>
    );
  }

  return (
    <img
      src={`/superfarmer/kostka/${sciana}.png`}
      alt={opis}
      className="w-4/5 h-4/5 object-contain"
      draggable={false}
      onError={() => setBlad(true)}
    />
  );
}

/**
 * Zwierzę w stadzie: kartonik z pudełka albo krążek psa.
 *
 * Psy są okrągłe, bo takie są w pudełku — i dobrze, bo dzięki temu w rzędzie
 * siedmiu pozycji od razu widać, które dwie nie liczą się do kompletu.
 */
export function Kartonik({
  zwierze,
  rozmiar = "zwykly",
}: {
  zwierze: string;
  rozmiar?: "zwykly" | "maly";
}) {
  const bok = rozmiar === "maly" ? "w-8 h-8" : "w-12 h-12";
  const okragly = zwierze === "malyPies" || zwierze === "duzyPies";
  return (
    <img
      src={`/superfarmer/${zwierze}.png`}
      alt=""
      draggable={false}
      className={[bok, okragly ? "rounded-full" : "rounded-lg", "object-cover"].join(" ")}
      style={{ background: "var(--kartonik-tlo)" }}
    />
  );
}
